import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ErrorModal from "../modals/ErrorModal";
import { useTranslation } from "react-i18next";
import { useLiffProfile } from "../../context/LiffProfileContext";
import LoadingBar from "../LoadingBar";

const OtpFormRegister = ({ registerData, onSubmit, onClose }) => {
  const {
    idCard,
    firstName,
    lastName,
    gender,
    birthDate,
    laser,
    mobilePhone,
    email,
    issuedStore,
    birthDateType,
    title,
    otpRef,
    consentStatus,
    contactPermission,
    contactPreference,
  } = registerData;

  const { t, i18n } = useTranslation();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const { authToken, userId } = useLiffProfile();
  const [error, setError] = useState(null);
  const [consentOtpRef, setConsentOtpRef] = useState(otpRef);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(300);
  const navigate = useNavigate();

  const clientAuth = process.env.REACT_APP_CLIENT_AUTH;
  const apiUrlVerify = process.env.REACT_APP_API_URL + "/api/users/verify";
  const apiUrlConfirm = process.env.REACT_APP_API_URL + "/api/users/confirm";

  const toBuddhistDate = (date) => {
    // ตัวอย่าง
    //toBuddhistDate("2015-00-00"); // "25580000"
    if (!date) return "";
    const [year, month = "00", day = "00"] = date.split("-");
    if (!year) return "";
    return parseInt(year, 10) + 543 + month + day;
  };

  const fetchOtpRef = async () => {
    const payload = {
      idCard,
      laser,
      firstName,
      lastName,
      mobilePhone,
      birthDate: toBuddhistDate(birthDate),
      ...(issuedStore ? { issuedStore } : {}),
    };
    try {
      const res = await axios.post(apiUrlVerify, payload, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-Client-Auth": clientAuth,
          "x-user-id": userId,
          "Accept-Language": i18n.language.toUpperCase(),
          "Content-Type": "application/json",
        },
      });

      const { otpRef } = res.data?.data || {};
      if (!otpRef) return setError("ไม่สามารถรับ OTP ได้");

      setConsentOtpRef(otpRef);
      //setCountdown(300);
    } catch {
      setError("เกิดข้อผิดพลาดในการขอ OTP");
    }
  };

  const isPayloadReady =
    idCard && mobilePhone && firstName && lastName && laser && birthDate;

  useEffect(() => {
    if (isPayloadReady && !consentOtpRef && countdown === 300) {
      fetchOtpRef();
    }
  }, [idCard, mobilePhone, firstName, lastName, laser, birthDate, issuedStore]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleOtpChange = (e, idx) => {
    const val = e.target.value;
    if (/[^0-9]/.test(val)) return;
    const nextOtp = [...otp];
    nextOtp[idx] = val;
    setOtp(nextOtp);
    if (val && idx < 5)
      document.getElementById(`otp-input-${idx + 1}`)?.focus();
    if (nextOtp.every((v) => v !== "")) handleVerify(nextOtp.join(""));
  };

  const handleResendOtp = async () => {
    setOtp(["", "", "", "", "", ""]);
    setError(null);
    setCountdown(300);
    // ต้องยิง verify ใหม่เพื่อให้ BE ส่ง SMS อีกครั้ง
    // ไม่เคลียร์ consentOtpRef ก่อน เพราะถ้า fetchOtpRef พลาด จะเหลือ ref ว่างจนยืนยันไม่ได้
    setIsLoading(true);
    try {
      await fetchOtpRef();
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (otpValue = otp.join("")) => {
    if (otpValue.length !== 6) return setError("กรุณากรอก OTP ให้ถูกต้อง");
    setIsLoading(true);

    const payload = {
      birthDateType,
      gender,
      idCard,
      mobilePhone,
      idCardRef: laser,
      birthDate,
      tName: firstName,
      tLastName: lastName,
      consentStatus,
      contactPermission,
      contactPreference,
      consentOtpRef,
      consentOtp: otpValue,
      language: i18n.language.toUpperCase(),
      idCardType: 1, // 1=ID Card
      country: "TH",
      nationality: 1, // 1=Thai
      title: "9", // 9=K.
      ...(email ? { email } : {}),
      ...(issuedStore ? { issuedStore } : {}),
    };

    try {
      const res = await axios.post(apiUrlConfirm, payload, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "X-Client-Auth": clientAuth,
          "x-user-id": userId,
          "Content-Type": "application/json",
          "Accept-Language": i18n.language.toUpperCase(),
        },
      });

      const { statusCode, message, data } = res.data;
      switch (statusCode) {
        case 200:
          // refresh user
          onSubmit(payload);
          setIsLoading(true);
          navigate("/SuccessBindingForm", { state: payload });
          break;
        case 400:
          switch (message) {
            case "INCORRECT_OTP":
              setError(t("incorrect_otp"));
              setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
              break;
            case "EXPIRED_OTP":
              setError(t("expired_otp"));
              setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
              break;
            default:
              setError(message);
              setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
          }
          break;
        case 404:
          setError(message);
          setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
          break;
        case 406:
          setError(t("register_error_code_406"));
          setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
          break;
        case 409:
          setError(t("register_error_code_409"));
          setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
          break;
        default:
          setError(message);
          setOtp(["", "", "", "", "", ""]); // <-- เพิ่มบรรทัดนี้
      }

      /*if (statusCode === 200) {
        onSubmit(payload);
        setIsLoading(true);
        navigate("/SuccessBindingForm", { state: payload });
      } else if (statusCode === 400) {
        if (message === "INVALID_STORE_ID") {
          setError(t("incorrect_store_code"));
        } else {
          setError(t("dopa_error_code_400"));
        }
      } else if (statusCode === 409) {
        setError(t("verify_registration_error_code_409"));
      } else if (statusCode === 503) {
        setError(t("dopa_error_code_503"));
      } else {
        setError(message || "ยืนยัน OTP ไม่สำเร็จ");
      }*/
    } /*(catch {
      setError("เกิดข้อผิดพลาดขณะยืนยัน OTP");
    } */ finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-sm w-full p-6 shadow-lg relative">
        <button
          className="absolute top-2 right-3 text-gray-500 text-xl"
          onClick={() => {
            setError(null);
            onClose();
          }}
        >
          &times;
        </button>
        <div className="text-3xl font-semibold">{t("validate_otp_header")}</div>
        <p className="text-lg mt-2">
          {t("validate_otp_description", {
            phone: `${mobilePhone?.slice(0, 3)}-xxx-x${mobilePhone?.slice(-3)}`,
          })}
        </p>
        {consentOtpRef && (
          <p className="text-center text-sm mt-4 text-primary-text">
            {t("otp_reference_code")} : {consentOtpRef}
          </p>
        )}
        <div className="flex justify-center mt-4 mb-2">
          {otp.map((digit, i) => (
            <input
              key={i}
              id={`otp-input-${i}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              value={digit}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, "");
                if (val.length > 1) return; // กันกรณีพิมพ์รวดเดียว/แปะหลายตัว
                handleOtpChange({ target: { value: val } }, i);
              }}
              onKeyDown={(e) => {
                // กันไม่ให้กดตัวอักษร
                if (
                  e.key.length === 1 && // เฉพาะ key ตัวอักษร/ตัวเลข
                  !/[0-9]/.test(e.key) && // ถ้าไม่ใช่เลข 0-9
                  ![
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData
                  .getData("text")
                  .replace(/[^0-9]/g, "");
                if (pasted.length === otp.length) {
                  // วางทีเดียว 6 หลัก
                  setOtp(pasted.split(""));
                  // focus ออก
                  document
                    .getElementById(`otp-input-${otp.length - 1}`)
                    ?.blur();
                  e.preventDefault();
                }
              }}
              maxLength={1}
              className="w-12 h-16 text-center border border-gray-300 mx-1 text-2xl rounded-lg"
            />
          ))}
        </div>

        <button
          onClick={handleVerify}
          className="w-full bg-primary text-white py-3 rounded-full mt-4"
        >
          {isLoading ? t("loading") : t("confirm")}
        </button>
        {countdown > 0 && (
          <div className="text-sm text-gray-500 text-center mt-1">
            รหัสจะหมดอายุใน {Math.floor(countdown / 60)}:
            {String(countdown % 60).padStart(2, "0")} นาที
          </div>
        )}
        {countdown === 0 && (
          <div className="flex justify-center items-center mt-2">
            <button
              className="underline text-base font-medium text-primary"
              onClick={handleResendOtp}
              type="button"
            >
              {t("resend_otp")}
            </button>
          </div>
        )}
        <LoadingBar loading={isLoading} label="Loading..." />
        {error && <ErrorModal error={error} onClose={() => setError(null)} />}
      </div>
    </div>
  );
};

export default OtpFormRegister;
